import { useRef } from 'react';
import styled from '@emotion/styled';

export interface ResizeHandleProps {
  /** Callback when resize starts */
  onResizeStart?: () => void;
  /** Callback during resize with the new target width */
  onResize: (newWidth: number) => void;
  /** Callback when resize ends with final width */
  onResizeEnd?: (newWidth: number) => void;
  /** Current width of the panel (for constraint calculations) */
  currentWidth: number;
  /** Minimum allowed width */
  minWidth?: number;
  /** Maximum allowed width */
  maxWidth?: number;
}

/**
 * A draggable resize handle for horizontally resizing panels.
 * Uses pointer capture for smooth, reliable drag interactions.
 * Positioned on the left edge of a panel to resize it by dragging left/right.
 */
export function ResizeHandle({
  onResizeStart,
  onResize,
  onResizeEnd,
  currentWidth,
  minWidth = 0,
  maxWidth = Infinity,
}: ResizeHandleProps): React.JSX.Element {
  const startRef = useRef<{ x: number; width: number } | null>(null);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);

    startRef.current = {
      x: e.clientX,
      width: currentWidth,
    };

    onResizeStart?.();
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!startRef.current) return;

    // Calculate delta - negative deltaX means dragging left (shrinking), positive means right (expanding)
    const deltaX = e.clientX - startRef.current.x;

    // Calculate new width with constraints
    const newWidth = startRef.current.width - deltaX; // Subtract because we're on the left edge
    const constrainedWidth = Math.max(minWidth, Math.min(maxWidth, newWidth));

    onResize(constrainedWidth);
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!startRef.current) return;

    const deltaX = e.clientX - startRef.current.x;
    const newWidth = startRef.current.width - deltaX;
    const constrainedWidth = Math.max(minWidth, Math.min(maxWidth, newWidth));

    onResizeEnd?.(constrainedWidth);
    startRef.current = null;
    e.currentTarget.releasePointerCapture(e.pointerId);
  };

  return (
    <ResizeEdge
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
    />
  );
}

// ─── Styled Components ───────────────────────────────────────────────────────

/**
 * Vertical strip on the left edge of a panel that allows horizontal resizing.
 * Shows visual feedback on hover with a blue tint and inset shadow.
 */
const ResizeEdge = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  bottom: 0,
  width: '10px',
  transform: 'translateX(-5px)', // Center on the edge (5px inside, 5px outside)
  cursor: 'ew-resize',
  zIndex: 10,
  transition: 'background-color 150ms, box-shadow 150ms',
  '&:hover': {
    backgroundColor: `${theme.palette.primary.main}1A`, // Light blue background
    boxShadow: `inset 3px 0 0 0 ${theme.palette.primary.main}`, // Blue edge indicator
  },
}));
